<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="element-header">
          Time management
        </h6>
      </div>
      <div class="element-box">
        <div class="d-flex justify-content-between mb-5">
          <h5>
            Mitarbeiter - Jahresübersicht (Vorgesetzte Übersicht)
          </h5>
          <button class="btn btn-primary custom_search_button2 w-auto ml-2"
                  @click="router.push({ name: 'monthlyOverviewCoordinator', params: { id } });">
            Monatsübersicht
          </button>
        </div>

        <Search
            :userDropdown="userDropdown"
            :filters="filters"
            :loading="isSearching"
            :yearsDropdown="yearsDropdown"
            @search="getCoordinatorAnnualOverview"/>

        <div class="row align-items-center justify-content-between mt-3">
          <div>
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getCoordinatorAnnualOverview"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>

            <ExcelExportButton
                :label="$t('general.excel_export_monthly_details')"
                :loading="isExcelDetailsLoaded"
                @click="getExcelExportDetails"/>
          </div>
        </div>
        <div class="table-responsive overflow-auto mt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Mitarbeiternr</th>
              <th class="text-right">Arbeitszeit</th>
              <th class="text-right">Soll</th>
              <th class="text-right">davon im Büro</th>
              <th class="text-right">Überstunden</th>
              <th class="text-right">Urlaub</th>
              <th class="text-right">Sonderurlaub</th>
              <th class="text-right">Krankheitstage</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(annualOverview) in coordinatorAnnualOverviewList.data" :key="annualOverview.id">
              <td>{{ annualOverview?.firstName + ' ' + annualOverview?.lastName }}</td>
              <td class="text-right">{{ annualOverview?.workedHours }}</td>
              <td class="text-right">{{ annualOverview?.planedHours }}</td>
              <td class="text-right">{{ annualOverview?.recalculatedPlanedHours }}</td>
              <td class="text-right">{{ annualOverview?.overtimeHours }}</td>
              <td class="text-right">{{ annualOverview?.numberOfVacationDays }}</td>
              <td class="text-right">{{ annualOverview?.numberOfSpecialLeaveDays }}</td>
              <td class="text-right">{{ annualOverview?.numberOfSickDays }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination v-if="coordinatorAnnualOverviewList?.pagination?.total_pages > 1"
                    :totalPages="coordinatorAnnualOverviewList?.pagination?.total_pages"
                    :currentPage="coordinatorAnnualOverviewList?.pagination?.current_page"
                    :totalItems="coordinatorAnnualOverviewList?.pagination?.total_items"
                    :perPage="coordinatorAnnualOverviewList?.pagination?.items_per_page"
                    @pagechanged="getCoordinatorAnnualOverview"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import {timeManagementService} from '@/api/TImeManagement';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import router from '@/router';
import {getUsersForCoordinatorOverviewDropdown, getYears} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Search from '@/views/App/TimeManagement/TMCoordinatorAnnualOverview/Search.vue';
import {onMounted, ref} from 'vue';
import ExcelExportButton from '@/components/ExcelExportButton.vue';
import {useRoute} from 'vue-router';

const route = useRoute();
const id = route.params.id;
const yearsDropdown = ref([]);
const userDropdown = ref([]);
const {enums} = piniaEnumsStore();
const isLoaded = ref(false);
const isExcelLoaded = ref(false);
const isExcelDetailsLoaded = ref(false);
const coordinatorAnnualOverviewList = ref([]);
const filters = ref({
  userId: '',
  year: yearsDropdown.value.at(-1)?.value || new Date().getFullYear(),
  month: enums.monthsInYear[0].value,
});

const isSearching = ref(false);

async function getExcelExport() {
  await downloadFile(
      '/time-management/annual-overview-excel-export-coordinator',
      undefined, {...filters.value}, false, 'GET', isExcelLoaded)
}

async function getExcelExportDetails() {
  await downloadFile(
      '/time-management/annual-details-overview-excel-export-coordinator',
      undefined, {...filters.value}, false, 'GET', isExcelDetailsLoaded)
}


async function getCoordinatorAnnualOverview(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  try {
    const perPageRes = coordinatorAnnualOverviewList.value?.pagination?.items_per_page;
    const {data} = await timeManagementService.getAnnualOverviewCoordinator({
          page: page,
          limit: perPage ?? perPageRes,
          ...(passedFilters ? passedFilters : filters.value),
        },
    );

    coordinatorAnnualOverviewList.value = data.data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

onMounted(async () => {
  await Promise.all([
    getYears(yearsDropdown),
    getCoordinatorAnnualOverview(),
    getUsersForCoordinatorOverviewDropdown(userDropdown),
  ]);

  isLoaded.value = true;
});

</script>
<style scoped>
.nav.sm {
  width: max-content;
}
</style>